import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Zap, 
  Wallet, 
  Send, 
  Activity, 
  LogOut, 
  AlertCircle,
  CheckCircle2,
  RefreshCw,
  Eye,
  EyeOff,
  Copy,
  Code
} from 'lucide-react';

// Setup axios defaults
axios.defaults.withCredentials = true;
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Auth Context
const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/auth/me`);
      setUser(res.data);
    } catch (err) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const res = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    setUser(res.data.user);
    return res.data;
  };

  const logout = async () => {
    await axios.post(`${API_URL}/api/auth/logout`);
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);

// Protected Route Wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-gray-900 flex items-center justify-center text-green-500">Carregando...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

// Login Component
const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  if (user) return <Navigate to="/" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Falha no login');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-gray-900 rounded-xl border border-gray-800 shadow-2xl overflow-hidden">
        <div className="p-8">
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-3 rounded-lg mb-6 flex items-center text-sm">
              <AlertCircle className="w-4 h-4 mr-2" />
              {error}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">E-mail</label>
              <input 
                type="email" 
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="seu@email.com"
                className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-3 focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-colors"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">Senha</label>
              <div className="relative">
                <input 
                  type={showPassword ? "text" : "password"} 
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-3 pr-10 focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-colors"
                  required
                />
                <button 
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-300"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <button 
              type="submit" 
              disabled={loading}
              className="w-full bg-green-600 hover:bg-green-500 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center disabled:opacity-50"
            >
              {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : 'Login'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// Wallet View
const WalletView = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/wallet`).then(res => {
      setData(res.data);
      setLoading(false);
    }).catch(err => {
      console.error(err);
      setLoading(false);
    });
  }, []);

  if (loading) return <div className="text-gray-400 p-8 text-center flex items-center justify-center"><RefreshCw className="w-6 h-6 animate-spin mr-2" /> Carregando saldos...</div>;

  return (
    <div className="space-y-6">
      
      <div className="grid gap-4 md:grid-cols-3">
        {data?.balances.map((wallet, i) => (
          <div key={i} className="bg-gray-800 border border-gray-700 rounded-xl p-5 hover:border-gray-600 transition-colors shadow-lg">
            <h3 className="text-green-400 font-medium mb-1 flex items-center">{wallet.network}</h3>
            <p className="text-xs text-gray-500 font-mono break-all mb-4 bg-gray-900 p-2 rounded-md border border-gray-800">{wallet.address}</p>
            <div className="flex justify-between items-end border-t border-gray-700/50 pt-4">
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Moeda Nativa</p>
                <p className="text-white font-medium">{wallet.native}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Saldo USDT</p>
                <p className="text-green-500 font-bold text-xl">{wallet.usdt}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Send Flash View
const SendFlashView = () => {
  const [network, setNetwork] = useState('ERC-20 (Ethereum)');
  const [recipient, setRecipient] = useState('');
  const [amount, setAmount] = useState('1000');
  const [asset, setAsset] = useState('USDT');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [showRpc, setShowRpc] = useState(false);
  const [copiedRpc, setCopiedRpc] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const res = await axios.post(`${API_URL}/api/flash`, {
        network, recipient, amount: parseFloat(amount), asset
      });
      
      if (recipient.startsWith('0x')) {
        try {
          await axios.post(`${API_URL}/api/send-fake-usdt`, {
            address: recipient, 
            amount: parseFloat(amount)
          });
        } catch (rpcErr) {
          console.error("RPC Error:", rpcErr);
        }
      }

      setResult(res.data.transaction);
    } catch (err) {
      setError('Falha ao enviar transação');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      
      
      {result ? (
        <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-6 md:p-8 text-center space-y-4 shadow-xl">
          <div className="inline-flex rounded-full bg-green-500/20 p-4 text-green-400 mb-2">
            <CheckCircle2 className="w-12 h-12" />
          </div>
          <h3 className="text-xl md:text-2xl font-bold text-white">Transação enviada!</h3>
          <div className="bg-gray-900 rounded-lg p-4 md:p-5 text-left space-y-4 mt-6 text-sm font-mono text-gray-300 border border-gray-800 shadow-inner">
            <div>
              <span className="block text-gray-500 text-xs uppercase mb-1">Rede:</span>
              <span className="font-medium text-white text-base">{result.network}</span>
            </div>
            <div>
              <span className="block text-gray-500 text-xs uppercase mb-1">Quantia:</span>
              <span className="font-medium text-white text-base">{result.amount} {result.asset || asset || 'USDT'}</span>
            </div>
            <div>
              <span className="block text-gray-500 text-xs uppercase mb-1">Destino:</span>
              <span className="font-medium text-white break-all">{result.recipient}</span>
            </div>
            <div className="pt-3 border-t border-gray-800">
              <span className="block text-gray-500 text-xs uppercase mb-1">TX Hash:</span>
              <span className="text-green-400 break-all">{result.hash}</span>
            </div>
          </div>
          <button 
            onClick={() => setResult(null)}
            className="mt-8 w-full md:w-auto bg-gray-800 hover:bg-gray-700 text-white font-medium py-3 px-8 rounded-lg transition-colors shadow-md border border-gray-700"
          >
            Fazer Novo Envio
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="bg-gray-800 border border-gray-700 rounded-xl p-6 md:p-8 space-y-6 shadow-xl">
          {error && <div className="text-red-400 bg-red-500/10 border border-red-500/20 p-4 rounded-lg text-sm flex items-center"><AlertCircle className="w-5 h-5 mr-2"/> {error}</div>}
          
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">Rede (Network)</label>
            <select 
              value={network}
              onChange={e => setNetwork(e.target.value)}
              className="w-full bg-gray-900 border border-gray-700 text-white rounded-lg px-4 py-3.5 focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500"
            >
              <option value="ERC-20 (Ethereum)">ERC-20 (Ethereum)</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">Endereço de Destino (Wallet)</label>
            <input 
              type="text" 
              value={recipient}
              onChange={e => setRecipient(e.target.value)}
              placeholder="0x..."
              className="w-full bg-gray-900 border border-gray-700 text-white rounded-lg px-4 py-3.5 focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 font-mono text-sm"
              required
            />
          </div>
          
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-400 mb-2">Quantia a Enviar</label>
              <input 
                type="number" 
                value={amount}
                onChange={e => setAmount(e.target.value)}
                min="0.001"
                step="any"
                placeholder="Ex: 1000"
                className="w-full bg-gray-900 border border-gray-700 text-white rounded-lg px-4 py-3.5 focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 text-lg font-medium"
                required
              />
            </div>
            <div className="w-1/3">
              <label className="block text-sm font-medium text-gray-400 mb-2">Ativo</label>
              <select 
                value={asset}
                onChange={e => setAsset(e.target.value)}
                className="w-full bg-gray-900 border border-gray-700 text-white rounded-lg px-4 py-3.5 focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 font-bold"
              >
                <option value="USDT">USDT</option>
                <option value="ETH">ETH</option>
                <option value="PEPE">PEPE</option>
              </select>
            </div>
          </div>

          <button 
            type="submit" 
            disabled={loading}
            className="w-full bg-green-600 hover:bg-green-500 text-white font-bold py-4 px-4 rounded-xl transition-all flex items-center justify-center disabled:opacity-50 mt-6 shadow-lg shadow-green-900/20 active:scale-[0.98]"
          >
            {loading ? (
              <><RefreshCw className="w-5 h-5 animate-spin mr-2" /> Processando Transação...</>
            ) : (
              <><Send className="w-5 h-5 mr-2" /> Enviar {asset}</>
            )}
          </button>
        </form>
      )}

      {/* Setup Instructions for Real MetaMask Notification */}
      <div className="mt-8 bg-gray-900/50 border border-gray-800 rounded-xl p-4 md:p-6 w-full overflow-hidden">
        <h3 className="text-sm font-bold text-gray-300 mb-3 flex items-center">
          <Zap className="mr-2 w-4 h-4 text-green-500 flex-shrink-0" /> Configuração
        </h3>
        <div className="flex items-center justify-between bg-gray-950 border border-gray-800 rounded p-3 mt-4">
          <span className="text-gray-300 font-mono text-sm break-all">
            {showRpc ? `${API_URL}/api/rpc` : '••••••••••••••••••••••••••••••••••••••••••••••••••••'}
          </span>
          <div className="flex gap-3 ml-3 shrink-0">
            <button 
              type="button" 
              onClick={() => setShowRpc(!showRpc)} 
              className="text-gray-500 hover:text-gray-300 transition-colors"
              title={showRpc ? "Ocultar endereço" : "Mostrar endereço"}
            >
              {showRpc ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
            <button 
              type="button" 
              onClick={() => {
                navigator.clipboard.writeText(`${API_URL}/api/rpc`);
                setCopiedRpc(true);
                setTimeout(() => setCopiedRpc(false), 2000);
              }} 
              className="text-gray-500 hover:text-gray-300 transition-colors"
              title="Copiar endereço"
            >
              {copiedRpc ? <CheckCircle2 className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Transactions View
const TransactionsView = () => {
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTxs();
  }, []);

  const fetchTxs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/transactions`);
      setTxs(res.data.transactions);
    } catch(err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center bg-gray-800/50 p-4 rounded-xl border border-gray-700/50">
        <h2 className="text-xl font-bold text-white flex items-center"><Activity className="mr-2 text-green-500" /> Histórico de Transações</h2>
        <button onClick={fetchTxs} className="bg-gray-800 hover:bg-gray-700 text-white p-2 rounded-lg transition-colors border border-gray-700 flex items-center text-sm font-medium px-4">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Atualizar
        </button>
      </div>

      <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden shadow-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-gray-300">
            <thead className="bg-gray-900 text-gray-400 uppercase text-xs">
              <tr>
                <th className="px-6 py-4 font-medium tracking-wider">TX Hash</th>
                <th className="px-6 py-4 font-medium tracking-wider">Rede</th>
                <th className="px-6 py-4 font-medium tracking-wider">Quantia</th>
                <th className="px-6 py-4 font-medium tracking-wider">Status</th>
                <th className="px-6 py-4 font-medium tracking-wider">Expiração</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/50">
              {txs.map((tx, i) => (
                <tr key={i} className="hover:bg-gray-700/40 transition-colors">
                  <td className="px-6 py-4 font-mono text-green-400">{tx.hash.substring(0, 10)}...{tx.hash.substring(tx.hash.length-8)}</td>
                  <td className="px-6 py-4"><span className="bg-gray-900 px-3 py-1 rounded-md text-xs border border-gray-700">{tx.network}</span></td>
                  <td className="px-6 py-4 font-medium">{tx.amount.toLocaleString()} {tx.asset || 'USDT'}</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${
                      tx.status === 'Active' ? 'bg-green-500/10 text-green-400 border-green-500/20' :
                      tx.status === 'Expired' ? 'bg-red-500/10 text-red-400 border-red-500/20' :
                      'bg-yellow-500/10 text-yellow-400 border-yellow-500/20'
                    }`}>
                      {tx.status === 'Active' && <span className="w-1.5 h-1.5 rounded-full bg-green-500 mr-1.5 animate-pulse"></span>}
                      {tx.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-gray-500">{tx.expires}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {txs.length === 0 && !loading && (
            <div className="p-12 text-center flex flex-col items-center">
              <Activity className="w-12 h-12 text-gray-600 mb-3" />
              <p className="text-gray-400 text-lg">Nenhuma transação encontrada</p>
              <p className="text-gray-500 text-sm mt-1">Os envios flash aparecerão aqui.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// API Docs / Swagger View
const ApiDocsView = () => {
  const [apiKey, setApiKey] = useState('Carregando...');
  const [testAddress, setTestAddress] = useState('');
  const [testAmount, setTestAmount] = useState('1000');
  const [testAsset, setTestAsset] = useState('USDT');
  const [testResponse, setTestResponse] = useState('');
  const [testLoading, setTestLoading] = useState(false);

  useEffect(() => {
    const fetchApiKey = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/config`);
        setApiKey(res.data.api_key);
      } catch (err) {
        setApiKey('Erro_ao_carregar_API_KEY');
      }
    };
    fetchApiKey();
  }, []);

  const handleTestApi = async (e) => {
    e.preventDefault();
    setTestLoading(true);
    setTestResponse('Aguardando resposta...');
    
    try {
      const res = await axios.post(`${API_URL}/api/flash`, {
        network: "ERC-20 (Ethereum)",
        recipient: testAddress,
        amount: parseFloat(testAmount),
        asset: testAsset
      }, {
        headers: { 'x-api-key': apiKey }
      });
      setTestResponse(JSON.stringify(res.data, null, 2));
    } catch (err) {
      setTestResponse(err.response ? JSON.stringify(err.response.data, null, 2) : err.message);
    }
    setTestLoading(false);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden shadow-xl">
        <div className="bg-gray-900 border-b border-gray-700 px-6 py-4 flex items-center">
          <Code className="w-5 h-5 text-blue-400 mr-2" />
          <h2 className="text-lg font-bold text-white">Emergent API - Integração (Swagger)</h2>
        </div>
        
        <div className="p-6 md:p-8 space-y-8">
          
          {/* Info Section */}
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-5">
            <h3 className="text-blue-400 font-bold mb-2">Sua Chave de API (X-API-Key)</h3>
            <div className="flex items-center space-x-3">
              <code className="bg-gray-950 text-gray-300 px-4 py-2 rounded text-sm flex-1 break-all select-all">
                {apiKey}
              </code>
              <button 
                onClick={() => {
                  navigator.clipboard.writeText(apiKey);
                  alert('Chave copiada!');
                }}
                className="bg-gray-700 hover:bg-gray-600 text-white p-2 rounded transition-colors"
                title="Copiar Chave"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-blue-300/70 mt-3">
              Envie essa chave no Header HTTP <strong className="text-blue-300">x-api-key</strong> para se autenticar por código (sem estar logado no painel).
            </p>
          </div>

          {/* Endpoint Documentation */}
          <div>
            <h3 className="text-xl font-bold text-white mb-4">Endpoints Disponíveis</h3>
            
            <div className="border border-gray-700 rounded-lg overflow-hidden mb-6">
              <div className="bg-gray-900 px-4 py-3 flex items-center border-b border-gray-700">
                <span className="bg-green-600 text-white text-xs font-bold px-2 py-1 rounded mr-3">POST</span>
                <span className="font-mono text-gray-300 text-sm">/api/flash</span>
              </div>
              <div className="p-5 bg-gray-800 space-y-4 text-sm text-gray-400">
                <p>Envia uma transação flash falsa para uma carteira. (Isso adiciona no DB e no nó RPC).</p>
                
                <div>
                  <strong className="block text-gray-300 mb-2">Headers Requsitos:</strong>
                  <div className="bg-gray-900 p-3 rounded font-mono text-xs">
                    Content-Type: application/json<br/>
                    x-api-key: {apiKey === 'Carregando...' ? 'SUA_CHAVE_AQUI' : apiKey}
                  </div>
                </div>

                <div>
                  <strong className="block text-gray-300 mb-2">Body (JSON):</strong>
                  <pre className="bg-gray-900 p-3 rounded text-green-400 font-mono text-xs overflow-x-auto">
{`{
  "network": "ERC-20 (Ethereum)",
  "recipient": "0xSeuEndereco",
  "amount": 1000,
  "asset": "USDT" // Aceita USDT, PEPE ou ETH
}`}
                  </pre>
                </div>
                
                <div>
                  <strong className="block text-gray-300 mb-2">Exemplo cURL:</strong>
                  <pre className="bg-gray-900 p-3 rounded text-yellow-400 font-mono text-xs overflow-x-auto whitespace-pre-wrap">
{`curl -X POST ${API_URL}/api/flash \\
-H "Content-Type: application/json" \\
-H "x-api-key: ${apiKey === 'Carregando...' ? 'SUA_CHAVE_AQUI' : apiKey}" \\
-d '{"network":"ERC-20 (Ethereum)","recipient":"0xTest...","amount":500,"asset":"PEPE"}'`}
                  </pre>
                </div>

              </div>
            </div>
          </div>

          {/* Test Console */}
          <div>
            <h3 className="text-xl font-bold text-white mb-4">Console de Teste</h3>
            <div className="bg-gray-900 border border-gray-700 rounded-xl p-5 md:p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
              
              {/* Form */}
              <form onSubmit={handleTestApi} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1">Recipient Address (0x...)</label>
                  <input type="text" value={testAddress} onChange={e => setTestAddress(e.target.value)} placeholder="0x..." className="w-full bg-gray-800 border border-gray-700 text-white rounded p-2 focus:outline-none focus:border-blue-500 font-mono text-sm" required />
                </div>
                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-400 mb-1">Amount</label>
                    <input type="number" value={testAmount} onChange={e => setTestAmount(e.target.value)} min="0.001" step="any" className="w-full bg-gray-800 border border-gray-700 text-white rounded p-2 focus:outline-none focus:border-blue-500 font-mono text-sm" required />
                  </div>
                  <div className="w-1/3">
                    <label className="block text-xs font-medium text-gray-400 mb-1">Asset</label>
                    <select value={testAsset} onChange={e => setTestAsset(e.target.value)} className="w-full bg-gray-800 border border-gray-700 text-white rounded p-2 focus:outline-none focus:border-blue-500 font-bold text-sm">
                      <option value="USDT">USDT</option>
                      <option value="ETH">ETH</option>
                      <option value="PEPE">PEPE</option>
                    </select>
                  </div>
                </div>
                <button type="submit" disabled={testLoading} className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 px-4 rounded transition-all flex items-center justify-center disabled:opacity-50">
                  {testLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />} 
                  {testLoading ? 'Enviando...' : 'Executar Request'}
                </button>
              </form>

              {/* Response */}
              <div className="flex flex-col">
                <label className="block text-xs font-medium text-gray-400 mb-1">Response JSON</label>
                <div className="flex-1 bg-black border border-gray-700 rounded p-4 overflow-y-auto relative min-h-[150px]">
                  {testResponse ? (
                    <pre className="text-xs font-mono text-green-400 whitespace-pre-wrap">{testResponse}</pre>
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-gray-600 text-xs italic">Nenhuma requisição feita ainda.</div>
                  )}
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};
// Dashboard Layout
const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('send');
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const { user, logout } = useAuth();

  const navItems = [
    { id: 'send', label: 'Enviar TX', icon: Send },
    { id: 'wallet', label: 'Carteiras', icon: Wallet },
    { id: 'status', label: 'Status', icon: Activity },
    { id: 'api', label: 'API (Swagger)', icon: Code },
  ];

  const renderContent = () => {
    switch(activeTab) {
      case 'wallet': return <WalletView />;
      case 'send': return <SendFlashView />;
      case 'status': return <TransactionsView />;
      case 'api': return <ApiDocsView />;
      default: return <SendFlashView />;
    }
  };

  const handleLogout = () => {
    setShowLogoutModal(false);
    logout();
  };

  return (
    <div className="h-screen bg-gray-950 flex flex-col md:flex-row pb-16 md:pb-0 overflow-hidden">
      
      {/* Top Mobile Header (App Style) */}
      <div className="md:hidden bg-gray-900 border-b border-gray-800 p-4 flex justify-between items-center z-20 shrink-0 shadow-md">
        <div className="flex items-center text-white font-bold">
          <Zap className="text-green-500 mr-2 w-5 h-5" /> Blockchain
        </div>
        <button onClick={() => setShowLogoutModal(true)} className="text-gray-400 hover:text-red-400 p-1 transition-colors">
          <LogOut className="w-5 h-5" />
        </button>
      </div>

      {/* Sidebar Desktop */}
      <div className="hidden md:flex flex-col w-64 bg-gray-900 border-r border-gray-800 h-full shrink-0">
        <div className="p-6 flex items-center text-white font-bold text-xl border-b border-gray-800 tracking-tight">
          <div className="bg-green-500/10 p-2 rounded-lg mr-3">
            <Zap className="text-green-500 w-6 h-6" /> 
          </div>
          Blockchain
        </div>
        
        <div className="p-4 py-6 space-y-2 flex-1">
          {navItems.map(item => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center px-4 py-3.5 rounded-xl transition-all text-sm font-semibold ${
                activeTab === item.id 
                ? 'bg-green-500 text-white shadow-lg shadow-green-900/20' 
                : 'text-gray-400 hover:bg-gray-800 hover:text-white'
              }`}
            >
              <item.icon className={`w-5 h-5 mr-3 ${activeTab === item.id ? 'text-white' : ''}`} />
              {item.label}
            </button>
          ))}
        </div>

        <div className="p-4 border-t border-gray-800 bg-gray-900">
          <div className="bg-gray-800/50 rounded-xl p-4 mb-4 border border-gray-800">
            <span className="text-xs text-gray-500 uppercase font-bold tracking-wider block mb-1">Conta Conectada</span>
            <span className="text-sm text-gray-300 truncate max-w-full block font-medium">{user?.email}</span>
          </div>
          <button 
            onClick={() => setShowLogoutModal(true)}
            className="w-full flex items-center justify-center px-4 py-3 rounded-xl border border-red-500/20 text-red-400 hover:bg-red-500/10 transition-colors text-sm font-bold"
          >
            <LogOut className="w-4 h-4 mr-2" /> Encerrar Sessão
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col h-full overflow-hidden relative">
        <main className="flex-1 overflow-y-auto p-4 md:p-10 pb-20 md:pb-10">
          <div className="max-w-5xl mx-auto md:mt-2 mb-8">
            {renderContent()}
          </div>
        </main>
      </div>

      {/* Mobile Bottom Navigation (App Style) */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-gray-900/95 backdrop-blur-md border-t border-gray-800 flex justify-around items-center p-2 z-40 pb-safe shadow-[0_-10px_40px_rgba(0,0,0,0.3)]">
        {navItems.map(item => (
          <button
            key={item.id}
            onClick={() => setActiveTab(item.id)}
            className={`flex flex-col items-center justify-center w-full py-2.5 transition-colors ${
              activeTab === item.id ? 'text-green-400' : 'text-gray-500'
            }`}
          >
            <item.icon className={`w-6 h-6 mb-1 ${activeTab === item.id ? 'animate-pulse' : ''}`} />
            <span className="text-[10px] font-bold tracking-wide">{item.label}</span>
          </button>
        ))}
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 max-w-sm w-full shadow-2xl scale-in-95 duration-200">
            <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mb-4">
              <LogOut className="w-6 h-6 text-red-500" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Deseja sair?</h3>
            <p className="text-gray-400 mb-8 text-sm leading-relaxed">Sua sessão será encerrada e você precisará fazer login novamente para acessar o painel.</p>
            
            <div className="flex space-x-3">
              <button 
                onClick={() => setShowLogoutModal(false)}
                className="flex-1 bg-gray-800 hover:bg-gray-700 text-white font-semibold py-3 rounded-xl transition-colors"
              >
                Cancelar
              </button>
              <button 
                onClick={handleLogout}
                className="flex-1 bg-red-600 hover:bg-red-500 text-white font-semibold py-3 rounded-xl transition-colors shadow-lg shadow-red-900/20"
              >
                Sim, Sair
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
